// frontend/src/navigation/SeitenleisteBearbeiten.jsx
import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import { alpha, keyframes } from '@mui/material/styles'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { Eye, EyeOff, GripVertical, RotateCcw, SlidersHorizontal, Star } from 'lucide-react'
import { FS, SP, TOUCH_TARGET_COARSE_QUADRAT, hoverOnly } from '../theme/tokens'
import { zuruecksetzen } from './sidebarPrefs'

/**
 * SeitenleisteBearbeiten — die Bausteine, die der Modus „Customize" braucht.
 *
 * @description
 *   Vier Auszuege aus `SidebarAndHeaderBar.jsx` (vor dem Auszug 1775 Zeilen), und
 *   der Grund ist die Dateigroesse, nicht Regel 12: die neue Datei liegt in
 *   `navigation/` mit EINEM Importeur, Regel 12 greift dort in keiner Lesart. Der
 *   JSDoc-Kopf und der Registereintrag stehen trotzdem hier, weil dies der
 *   Baustein ist, den der naechste wiederverwenden will.
 *
 *   🔴 Der Modus zieht an die ECHTE Leiste (Plan §E1). Es gibt keine zweite Liste
 *   mehr: die Eintraege der Hauptnavigation tragen im Modus selbst ihre
 *   Bedienelemente (Griff · Auge · Stern), ausgeblendete Eintraege bleiben an
 *   ihrem Platz stehen und sind gedimmt. `SeitenleisteBearbeiten` ist nur noch
 *   die Huelle: der Knopf „Customize"/„Done", „Reset" und — nur im
 *   Modus — der `DragDropContext` um die uebergebenen Zeilen.
 *
 * @useWhen
 *   - Eine Zeile der Hauptnavigation gerendert wird (Icon · Label · Zaehler)
 *   - Der Customize-Modus der Seitenleiste gebraucht wird
 *
 * @dontUseFor
 *   - Die mobile Leiste (`MobileBottomNav`): sie hat eine eigene Literalliste
 *     und fuenf feste Plaetze, kein Customize
 *
 * @related
 *   - `navItems.jsx` — der Katalog, aus dem `icon` und `label` kommen
 *   - `sidebarPrefs.js` — die Regeln, die der Modus anzeigt (er besitzt sie nicht)
 */

/**
 * Der zugängliche Zusatz der Favoriten-Kopie, unsichtbar.
 *
 * 🔴 Der Zusatz ist ein EIGENER Textknoten und wird NICHT mit dem sichtbaren
 *   Label in denselben Knoten konkateniert (Plan §3.4, W5). Grund, gemessen:
 *   `getByRole('button', { name: 'Cases' })` macht einen exakten
 *   Namensvergleich — „Cases (Favorit)" ≠ „Cases", also genau EIN Treffer.
 *
 * 🔴 Die Einheiten sind Strings (`'1px'`), nicht Zahlen: MUI wendet seine
 *   Groessen-Transformation auf einheitenlose Werte an (`width: 1` wird `100%`),
 *   und dann waere das Rezept eine bildschirmfuellende Flaeche. Waechter:
 *   `components/common/srOnlyUnitlessSx.test.js`.
 */
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}

// 🔴 Regel 22 Nr. 8: Tippziele >= 44 px bei grobem Zeiger. Die Zeile traegt im
// Modus drei Bedienelemente (Griff · Auge · Stern), die Favoriten-Zeile zwei
// (Griff · Stern) — alle drei benutzen denselben Riegel (Plan §3.5, N6).
const ANPASSEN_TIPPZIEL = TOUCH_TARGET_COARSE_QUADRAT

/**
 * Ein Bedienfeld der Aktionsspalte. Die Groesse ist nicht Kosmetik, sondern die
 * Fluchtlinie: `IconButton size="small"` ist 14 px Symbol plus 2 x 5 px
 * Polsterung = 24 px (SP.md). Der PLATZHALTER der Favoriten-Zeile traegt
 * dieselbe Breite — ohne ihn ruecke der Stern dort um eine Knopfbreite nach
 * rechts und die Spalte fluchtete nicht (Owner-Befund: „Aktionsspalte springt").
 *
 * Das LABEL darf dagegen bis auf 0 schrumpfen (W14).
 */
const BEDIENFELD = {
  flexShrink: 0,
  width: (t) => t.spacing(SP.md),
  height: (t) => t.spacing(SP.md),
}

/**
 * Die Aktionsspalte rechts — Auge (oder Platzhalter) und Stern.
 *
 * 🔴 EIN Behaelter, nicht zwei Geschwister: nur so ist der Abstand INNEN
 *   (`SP.chip`) unabhaengig vom Zeilenabstand (`SP.tight`) und die Spalte in
 *   jeder Zeile gleich breit. Der Abstand der Zeile spart das ausserdem ein —
 *   zwei Kinder statt drei, eine Luecke weniger, ~4 px mehr fuer das Etikett.
 */
const AKTIONSSPALTE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: SP.chip,
  flexShrink: 0,
}

/**
 * Das Wackeln des Bearbeitungsmodus (Plan §E3).
 *
 * 🔴 Transform-only, keine Layout-Animation. ±1,5°, ~260 ms, endlos.
 *
 * 🔴 `animation: none` bei `prefers-reduced-motion: reduce` — STATT der Bewegung
 *   tritt ein gestrichelter Rahmen, ausdruecklich gesetzt. Eine Animation, die
 *   nur „kaum sichtbar" ist, ist fuer Menschen mit Vestibularstoerung trotzdem
 *   eine Animation; der Zweig ersetzt sie, er daempft sie nicht.
 *
 * 🔴 Die Animation sitzt auf dem ETIKETT (`ListItemText`), nicht auf der Zeile
 *   und nicht auf dem Draggable-Wrapper. Drei Gruende, alle gemessen bzw.
 *   ausdruecklich entschieden:
 *   1. Die Zieh-Bibliothek schreibt ihr `transform` inline auf den Wrapper, und
 *      eine Keyframe-Regel auf demselben Knoten ueberschriebe es (Plan §6.2, W2).
 *   2. Auf der ZEILE wackelten Griff, Auge und Stern mit — bewegte Ziele trifft
 *      man schlechter, und Playwright wartet auf `stable` und laeuft in einen
 *      Timeout (Owner-Befund: „Wackeln trifft die Bedienknoepfe").
 *   3. Auf dem Etikett steht jedes Bedienelement in einem EIGENEN
 *      Transform-Kontext und bleibt stehen.
 *
 * 🔴 Der gestrichelte Ersatzrahmen sitzt dagegen auf der ZEILE (`RUHIG_RAHMEN`)
 *   und nicht auf dem Etikett: er ist die Aussage „diese Zeile laesst sich
 *   bewegen", und die gilt der ganzen Zeile. Um das Etikett gelegt saehe er aus
 *   wie ein Eingabefeld. `box-sizing: border-box` (MUI, global) haelt ihn
 *   layoutneutral.
 */
const WACKELN_KEYFRAMES = keyframes`
  0% { transform: rotate(-1.5deg); }
  50% { transform: rotate(1.5deg); }
  100% { transform: rotate(-1.5deg); }
`

const WACKELN = { animation: `${WACKELN_KEYFRAMES} 260ms ease-in-out infinite` }

/**
 * Der Ersatz fuer das Wackeln bei `prefers-reduced-motion` — auf der ZEILE.
 *
 * 🔴 `1px dashed` + `borderColor: 'divider'` (Token). Ein roher Farbwert waere
 *   ein Regel-22-Verstoss in einer bisher sauberen Datei.
 */
const RUHIG_RAHMEN = {
  '@media (prefers-reduced-motion: reduce)': {
    border: '1px dashed',
    borderColor: 'divider',
  },
}

/**
 * Der statische Griff-Hinweis.
 *
 * 🔴 Nur der STATISCHE Text ist austauschbar. Die Bewegungsansagen der
 *   Bibliothek sind modulintern und fuehren kein Prop
 *   (`screen-reader-message-preset.ts:10-15`); ein zweiter `aria-live`-Bereich
 *   waere eine Ansage ZU VIEL je Zug und ist deshalb bewusst nicht gebaut
 *   (Plan §3.5, W4).
 *
 * 🔴 Englisch seit dem Sprachwechsel (Owner-Entscheid 2026-10-06: die
 *   Oberflaeche ist englisch). Bis dahin war er deutsch, und damit war die
 *   Bedienung zweisprachig — der Hinweis deutsch, die Ansagen der Bibliothek
 *   englisch. Der Sprachwechsel loest genau diese Restluecke auf.
 */
const GRIFF_ANWEISUNG =
  'Press space to lift the entry, move it with the arrow keys, press space ' +
  'again to drop it, and press Escape to cancel.'

/**
 * SeitenleisteZeile — EINE Zeile der Hauptnavigation.
 *
 * @description
 *   Icon · Label · Zaehler, mit der Aktiv-Markierung (Flaeche, Hover,
 *   `aria-current`). Im Bearbeitungsmodus traegt sie zusaetzlich den Ziehgriff
 *   (links) und die Bedienelemente Auge und Stern (rechts).
 *
 *   🔴 Zaehler und Flaechenradius kommen von aussen, und beide Gruende sind
 *   verschieden: der Zaehler haengt am PFAD, nicht an der Zeile; der Radius ist
 *   ein Gestaltungswert der Liste. Beides als Prop zu fuehren haelt diese Datei
 *   literal-frei — `check_ui_tokens.py` meldet jede neu angelegte Datei mit
 *   einem rohen Wert als „vorher sauber" und geht rot (Regel 22).
 *
 *   🔴 Im Modus ist die Zeile KEIN Knopf: `component="div"`, kein `role`,
 *   `tabIndex={-1}`. Sonst laege ein `<div role="button">` mit drei echten
 *   Knoepfen darin — ein Bedienelement im Bedienelement, und die Tastatur
 *   haette je Zeile vier Haltepunkte statt drei.
 *
 * @useWhen
 *   - Ein Navigationseintrag in der Desktop-Seitenleiste gezeichnet wird
 *
 * @dontUseFor
 *   - Ein Eintrag ohne Ziel (`action`) — das ist das mobile Blatt
 *
 * @related
 *   - `SidebarAndHeaderBar.jsx` — der Aufrufer; er kennt Route, Abschnitt und Voreinstellung
 *
 * @reuseHints
 *   - Die Favoriten-Zeile ist dieselbe Zeile mit `zusatzName` und ohne Auge
 *
 * @props
 *   icon          ReactNode — aus `navItem(path).icon` (im Modus ungenutzt)
 *   label         string    — aus `navItem(path).label`
 *   active        boolean   — sichtbare Aktiv-Markierung (der LAENGSTE passende
 *                             Eintrag gewinnt; `istAktuelleSeite`)
 *   ariaCurrent   boolean   — traegt DIESE Zeile `aria-current="page"`?
 *   onClick       fn        — Navigation (im Modus wirkungslos)
 *   badge         ReactNode — der Zaehler, oder null (im Modus ungenutzt)
 *   sx            object    — Flaechen-Anpassungen des Aufrufers (Zeilenradius)
 *   modus         boolean   — Bearbeitungsmodus? steuert Rolle, Tippziele, Griff
 *   gedimmt       boolean   — ausgeblendet, im Modus an seinem Platz sichtbar
 *   auge          object    — `{ gedrueckt, gesperrt, label, titel, onClick }`, oder null.
 *                             Im Modus steht statt des Auges ein gleich breiter
 *                             PLATZHALTER — sonst fluchtete der Stern nicht.
 *   stern         object    — `{ gedrueckt, label, titel, onClick }`, oder null
 *   zusatzName    string    — unsichtbarer Namenszusatz („ (favorite)")
 *   titel         string    — `title`-Attribut der Zeile (Heimatzeile eines Favoriten)
 *   wackeln       boolean   — Modus-Wackeln an? (und NICHT beim Ziehen). Traegt
 *                             die Etikett-Animation UND den gestrichelten
 *                             Ersatzrahmen der Zeile bei reduzierter Bewegung
 *   wackelVersatz number    — Phasenversatz der Zeile, damit sie nicht gleichzeitig wackeln
 *   isDragging    boolean   — zieht gerade? Der Griff zeigt dann `grabbing`
 */
export function SeitenleisteZeile({
  icon, label, active = false, ariaCurrent = false, onClick, badge = null, sx = null,
  modus = false, gedimmt = false,
  griffProps = null, auge = null, stern = null,
  zusatzName = null, titel = null, wackeln = false, wackelVersatz = 0, isDragging = false,
}) {
  const grund = {
    py: 0.75,
    px: 1.5,
    gap: SP.tight,
    mb: 0.25,
    alignItems: 'center',
    bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.08) : 'transparent',
    ...hoverOnly({
      bgcolor: active
        ? (t) => alpha(t.palette.primary.main, 0.12)
        : 'action.hover',
    }),
    transition: 'background-color 0.2s',
  }

  return (
    <ListItemButton
      onClick={modus ? undefined : onClick}
      component={modus ? 'div' : undefined}
      // 🔴 `ButtonBase` setzt `role="button"`, sobald `component !== 'button'`.
      // Im Modus MUSS das weg: die Zeile ist dann kein Bedienelement mehr,
      // sondern der Traeger dreier echter Knoepfe (Griff · Auge · Stern) —
      // sonst laege ein `role="button"` um drei Knoepfe. Ausserhalb bleibt es.
      role={modus ? undefined : 'button'}
      tabIndex={modus ? -1 : 0}
      disableRipple={modus}
      title={titel || undefined}
      // R16-A C1 / R16-B W4: der aktive Eintrag ist sonst NUR ueber Farbe und
      // Schriftgewicht erkennbar. `aria-current` ist die Auszeichnung fuer „die
      // aktuelle Seite" — und nur der LAENGSTE Treffer traegt sie (C-1), nicht
      // Eltern+Kind.
      aria-current={ariaCurrent ? 'page' : undefined}
      // 🔴 Die SICHTBARE Markierung war bis zum 2026-10-07 nur ueber Farbe und
      // Schriftgewicht zu sehen und damit in keiner Probe falsifizierbar. Sie
      // spiegelt sich jetzt in einem Attribut — die Zusicherung „genau EIN
      // Eintrag ist markiert" wird damit pruefbar, ohne einen Palettenwert
      // festzuschreiben.
      data-active={active ? 'true' : undefined}
      sx={[grund, gedimmt ? { opacity: 0.7 } : null, wackeln ? RUHIG_RAHMEN : null, sx]}
    >
      {modus && (
        <IconButton
          size="small"
          // 🔴 Der Namenszusatz der Kopie gehoert AUCH an die Bedienelemente:
          // im Modus ist die Zeile kein benanntes Element mehr, also traegt der
          // Griff sonst zweimal „Move Cases" — einmal in den Favoriten, einmal
          // im Abschnitt. Zwei gleichnamige Knoepfe mit verschiedener Wirkung
          // sind WCAG 4.1.2, und die Spec koennte die Kopie nicht adressieren.
          aria-label={`Move ${label}${zusatzName || ''}`}
          // 🔴 Waehrend des Zuges `grabbing` (R16 H5): der Griff sagt damit, dass
          // er gerade fuehrt — sonst bliebe er `grab`, als laege er still.
          sx={[BEDIENFELD, ANPASSEN_TIPPZIEL, { cursor: isDragging ? 'grabbing' : 'grab' }]}
          // `{...dragHandleProps}` zuletzt: die Bibliothek setzt `tabIndex`,
          // `role` und `aria-describedby` (den statischen Hinweis) auf den Griff.
          {...(griffProps || {})}
        >
          <GripVertical size={14} strokeWidth={2} />
        </IconButton>
      )}

      {/* 🔴 Im Modus ist das Navigationssymbol AUS und der Griff nimmt seinen
          Platz ein (Owner-Entscheid 2026-10-06). Zwei Symbole nebeneinander —
          Griff UND Zielsymbol — kosteten ~18 px, und die fehlten dem Etikett. */}
      {!modus && (
        <ListItemIcon
          sx={{
            minWidth: 0,
            color: active ? 'primary.main' : 'text.disabled',
          }}
        >
          {icon}
        </ListItemIcon>
      )}

      <ListItemText
        primary={(
          <>
            {label}
            {zusatzName ? <Box component="span" sx={SR_ONLY}>{zusatzName}</Box> : null}
          </>
        )}
        primaryTypographyProps={{
          fontSize: FS.md,
          fontWeight: active ? 600 : 400,
          color: active ? 'primary.main' : 'text.secondary',
          noWrap: true,
        }}
        // Das Wackeln traegt NUR das Etikett — siehe WACKELN oben.
        style={wackeln ? { animationDelay: `${-(wackelVersatz % 5) * 52}ms` } : undefined}
        sx={[wackeln ? WACKELN : null]}
      />

      {modus && (
        <Box sx={AKTIONSSPALTE}>
          {auge ? (
            <Tooltip title={auge.titel}>
              <IconButton
                size="small"
                sx={[BEDIENFELD, ANPASSEN_TIPPZIEL]}
                aria-label={auge.label}
                // W12: gesperrt per `aria-disabled`, NICHT per `disabled` — ein
                // `disabled`-Knopf ist nicht fokussierbar, sein Tooltip damit fuer
                // Tastatur und Screenreader unerreichbar.
                aria-disabled={auge.gesperrt || undefined}
                aria-pressed={auge.gedrueckt}
                onClick={auge.gesperrt ? undefined : auge.onClick}
              >
                {/* 🔴 Das Symbol zeigt den ZUSTAND, der Name die AKTION
                    (Owner-Entscheid 2026-10-06). Sichtbar = offenes Auge,
                    ausgeblendet = durchgestrichenes Auge. Vorher war es
                    umgekehrt — auf einen Blick sah es aus, als sei alles
                    ausgeblendet ausser dem einen Eintrag. */}
                {auge.gedrueckt
                  ? <EyeOff data-testid="auge-ausgeblendet" size={14} strokeWidth={2} />
                  : <Eye data-testid="auge-sichtbar" size={14} strokeWidth={2} />}
              </IconButton>
            </Tooltip>
          ) : (
            /* Platzhalter der Favoriten-Zeile: sie traegt kein Auge (das einer
               Kopie waere mehrdeutig, Plan §3.4), braucht aber denselben Platz,
               damit der Stern in JEDER Zeile an derselben Kante steht. */
            <Box aria-hidden="true" sx={[BEDIENFELD, ANPASSEN_TIPPZIEL]} />
          )}

          {stern && (
            <Tooltip title={stern.titel}>
              <IconButton
                size="small"
                sx={[BEDIENFELD, ANPASSEN_TIPPZIEL]}
                aria-label={stern.label}
                aria-pressed={stern.gedrueckt}
                onClick={stern.onClick}
              >
                <Star size={14} strokeWidth={2} fill={stern.gedrueckt ? 'currentColor' : 'none'} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* 🔴 Im Modus sind die Abzeichen AUS (Owner-Entscheid 2026-10-06): ein
          Zaehler schiebt die Aktionsspalte und bringt die Spalten zum Springen. */}
      {!modus && badge}
    </ListItemButton>
  )
}

/**
 * SortierbareZeile — macht eine Zeile ziehbar, aber NUR im Modus.
 *
 * @description
 *   Ausserhalb des Modus gibt es keinen `DragDropContext`, also auch kein
 *   `Draggable` — die Funktion ruft dann nur `zeile(null, false)` und der
 *   Wrapper-Knoten entfaellt. Das haelt den Normalbetrieb unveraendert.
 *
 *   🔴 Die Animation sitzt am ETIKETT (in `SeitenleisteZeile`), nicht auf dem
 *   Wrapper: die Bibliothek schreibt ihr `transform` inline hierher, und eine
 *   Keyframe-Regel auf demselben Knoten ueberschriebe es (Plan §6.2, W2).
 *   Auf der Zeile wackelten ausserdem Griff, Auge und Stern mit.
 *
 *   🔴 `disableInteractiveElementBlocking` ist PFLICHT, nicht Zierrat — und sie
 *   ist der Grund, warum A.6/A.7 ueberhaupt scheiterten. Der Griff ist ein
 *   MUI-`IconButton`, also ein echtes `<button>`. `@hello-pangea/dnd` lehnt ein
 *   Abheben ab, sobald im Pfad vom Ereignisziel bis zum Draggable ein
 *   interaktives Element liegt (`isEventInInteractiveElement` →
 *   `isAnInteractiveElement`, `interactiveTagNames` enthaelt `button`): der
 *   Ereignisziel IST der Knopf, also greift der Waechter. Das gilt fuer BEIDE
 *   Sensoren — der Tastaturzug (`useKeyboardSensor`) UND der Mauszug
 *   (`useMouseSensor`) rufen `tryGetLock` mit `sourceEvent` auf und sterben an
 *   derselben Zeile. Die Bibliothek kennt fuer genau diesen Fall den Schalter
 *   (`props.disableInteractiveElementBlocking` → `options.canDragInteractiveElements`).
 *
 *   ⚠️ Er oeffnet nichts: ziehen laesst sich weiterhin NUR am Griff, denn
 *   `findClosestDraggableId` sucht das `data-rfd-drag-handle-*`-Merkmal und das
 *   tragen Auge und Stern nicht. Ein Klick auf den Stern hebt also nicht ab.
 *
 * @props
 *   modus       boolean — Modus an?
 *   draggableId string  — `${bereichId}::${path}` (NIE nur der Pfad, §3.3)
 *   index       number  — Position im Bereich
 *   zeile       fn      — `(dragHandleProps, isDragging) => ReactNode`
 */
export function SortierbareZeile({ modus, draggableId, index, zeile }) {
  if (!modus) return zeile(null, false)
  return (
    <Draggable draggableId={draggableId} index={index} disableInteractiveElementBlocking>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          {zeile(provided.dragHandleProps, snapshot.isDragging)}
        </div>
      )}
    </Draggable>
  )
}

/**
 * Zeilenbereich — die Liste EINES Abschnitts, im Modus ein Ablageziel.
 *
 * @description
 *   Ausserhalb des Modus eine schlichte `List`. Im Modus ein `Droppable`, dessen
 *   `isDropDisabled` der Aufrufer stellt — das ist der DOM-Riegel gegen den Zug
 *   ueber die Abschnittsgrenze (Plan §3.3, B1).
 *
 * @props
 *   bereichId      string  — `droppableId` (Abschnitts-Id oder `FAVORITEN`)
 *   modus          boolean
 *   isDropDisabled boolean — Ziel gesperrt?
 *   sx             object  — Flaechen-Anpassungen der Liste
 *   children       node
 */
export function Zeilenbereich({ bereichId, modus, isDropDisabled = false, sx = null, children }) {
  if (!modus) {
    return <List disablePadding sx={sx}>{children}</List>
  }
  return (
    <Droppable droppableId={bereichId} isDropDisabled={isDropDisabled}>
      {(provided) => (
        <List disablePadding sx={sx} ref={provided.innerRef} {...provided.droppableProps}>
          {children}
          {provided.placeholder}
        </List>
      )}
    </Droppable>
  )
}

/**
 * SeitenleisteBearbeiten — der Customize-Modus als Huelle um die echte Leiste.
 *
 * @description
 *   Der PERSOENLICHE Geltungsbereich. Geteilt bleibt, was die Verwaltung
 *   definiert — Abschnitte, Beschriftungen, Symbole; hier aendert der Nutzer nur,
 *   WAS er sieht und in welcher Reihenfolge.
 *
 *   🔴 Diese Komponente BESITZT keine Liste mehr. Sie rendert den Knopf, den
 *   Reset-Weg und — im Modus — den `DragDropContext` um `children`. Die
 *   Zeilen selbst kommen vom Aufrufer; die Regeln stehen in `sidebarPrefs.js`.
 *
 *   🔴 Esc schliesst den Modus NUR, wenn weder gezogen wird noch die Taste schon
 *   verbraucht ist (`event.defaultPrevented`). Die Zieh-Bibliothek bindet ihren
 *   Tastatur-Sensor waehrend eines Zuges auf `window` in der Capture-Phase und
 *   ruft `preventDefault()` ohne `stopPropagation()` (Plan §3.3, F2) — unser
 *   Listener am Dokument sieht die Taste also mit gesetztem `defaultPrevented`.
 *   Ergebnis: **Esc bricht den Zug ab, der Modus bleibt offen**; ein zweites Esc
 *   schliesst ihn.
 *
 *   🔴 Beim Austritt geht der Fokus ausdruecklich an den „Customize"-Knopf zurueck
 *   (W12): Griff, Auge und Stern verschwinden beim Schliessen, und ohne die
 *   Rueckgabe faellt der Fokus auf `<body>` — die Tastatur stuende am Anfang.
 *
 * @useWhen
 *   - Der Customize-Modus der Desktop-Seitenleiste gerendert wird
 *
 * @dontUseFor
 *   - Mobil — die Seitenleiste wird unter `sm` gar nicht montiert
 *     (`SidebarAndHeaderBar.jsx:1814`, `:2437`)
 *
 * @related
 *   - `sidebarPrefs.js` — die Regeln (Invariante, Reihenfolge), die hier nur sichtbar werden
 *
 * @props
 *   offen         boolean — ist der Modus offen? (Knopf heisst dann „Done")
 *   onUmschalten  fn      — oeffnet/schliesst ihn
 *   prefs         object  — die Voreinstellung (`holePrefs()`)
 *   setzePrefs    fn      — schreibt sie (speichert UND setzt den Zustand)
 *   children      node    — die echte Leiste (Abschnitte, in S5 auch Favoriten)
 *   onDragStart   fn      — `(start) => void`, merkt sich den Quellabschnitt
 *   onDragEnd     fn      — `(result) => void`, wendet die Zug-Regel an
 */
export function SeitenleisteBearbeiten({
  offen, onUmschalten, prefs, setzePrefs, children, onDragStart, onDragEnd,
}) {
  const knopfRef = useRef(null)
  const warOffen = useRef(false)
  const [ziehtGerade, setZiehtGerade] = useState(false)

  // W12 — Fokus-Rueckgabe an den Knopf, der montiert bleibt.
  useEffect(() => {
    if (warOffen.current && !offen) knopfRef.current?.focus()
    warOffen.current = offen
  }, [offen])

  // Esc schliesst — ausser die Bibliothek hat die Taste verbraucht oder es wird gezogen.
  useEffect(() => {
    if (!offen) return undefined
    const handler = (e) => {
      if (e.key !== 'Escape') return
      if (ziehtGerade || e.defaultPrevented) return
      onUmschalten()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [offen, ziehtGerade, onUmschalten])

  const hatAenderungen = prefs.versteckt.length > 0
    || Object.keys(prefs.reihenfolge).length > 0
    || (prefs.favoriten || []).length > 0

  const leiste = offen ? (
    <DragDropContext
      dragHandleUsageInstructions={GRIFF_ANWEISUNG}
      onDragStart={(start) => { setZiehtGerade(true); onDragStart?.(start) }}
      onDragEnd={(result) => { setZiehtGerade(false); onDragEnd?.(result) }}
    >
      {children}
    </DragDropContext>
  ) : children

  return (
    <>
      {leiste}
      <Box sx={{ px: 1.5, pt: 1, pb: 2 }}>
        <Button
          ref={knopfRef}
          size="small"
          fullWidth
          startIcon={<SlidersHorizontal size={16} strokeWidth={1.75} />}
          onClick={onUmschalten}
          // 🔴 Zustandswechsel, kein Disclosure (R16 H3): der Knopf schaltet den
          // Modus EIN/AUS, er klappt nichts auf. `aria-expanded` gehoert an den
          // Abschnittskopf (der klappt wirklich), hier ist `aria-pressed` richtig.
          aria-pressed={offen}
          sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}
        >
          {offen ? 'Done' : 'Customize'}
        </Button>

        {/* 🔴 `zuruecksetzen()` raeumt seit S1 AUCH die Favoriten (`LEER` traegt
            `favoriten: []`). Ohne `favoriten` in der Bedingung bekaeme, wer NUR
            Favoriten setzt (nichts ausgeblendet, nichts umsortiert), keinen Weg
            zurueck (R16 W2). */}
        {offen && hatAenderungen && (
          <Button
            size="small"
            startIcon={<RotateCcw size={16} strokeWidth={1.75} />}
            onClick={() => setzePrefs(zuruecksetzen())}
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Reset
          </Button>
        )}
      </Box>
    </>
  )
}
